import {
	formatQueryId,
	parseTimestamp,
	queryCategoryLabel,
	USER_TYPE_LABELS,
	workStatusLabel,
} from "@anthra/platform/domain"
import { toDisplayable } from "@anthra/platform/errors"
import { Button } from "@anthra/ui/button"
import { EmptyState } from "@anthra/ui/feedback"
import { Pagination } from "@anthra/ui/pagination"
import { RecordList } from "@anthra/ui/record-list"
import { Skeleton } from "@anthra/ui/skeleton"
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
	TableScroller,
} from "@anthra/ui/table"
import { PageTitle } from "@anthra/ui/typography"
import { useIsTabletUp } from "@anthra/ui/use-media-query"
import { createFileRoute, Link, useRouter } from "@tanstack/react-router"

import { StackedInstant } from "@/components/stacked-instant"
import { UserWithOwnNumber } from "@/components/user-with-own-number"
import { copy } from "@/copy/expert-queries-dashboard"
import { ItemBadge } from "@/features/activity/item-badge"
import { ElapsedCell } from "@/features/queries/elapsed-cell"
import { QueryFilters } from "@/features/queries/query-filters"
import { useExpertQueries } from "@/features/queries/use-expert-queries"
import { hasActiveFilters, useQueryFiltersStore } from "@/stores/use-query-filters-store"

/**
 * The All-Queries dashboard (US-01-001.1/.2/.10/.21/.27).
 *
 * Built against screens/expert-queries-dashboard.html: the search-and-filter
 * form, the result line with its clearable chips, and the shared table.
 *
 * ONE SHARED POOL. Every authenticated Expert sees every query, there is no
 * per-Expert filtering and no assignment step — so no request from this screen
 * carries an expert id.
 *
 * THE MOBILE NUMBER IS NOT IN THE TABLE. It is searchable and it appears in full
 * on the detail view, but a list of rows must never be a list of numbers
 * (US-01-001.21).
 */
export const Route = createFileRoute("/_panel/")({
	component: AllQueries,
})

function AllQueries() {
	const router = useRouter()
	const filters = useQueryFiltersStore(state => state.filters)
	const page = useQueryFiltersStore(state => state.page)
	const setPage = useQueryFiltersStore(state => state.setPage)
	const clearFilters = useQueryFiltersStore(state => state.clearFilters)

	const queries = useExpertQueries(filters, page)
	const rows = queries.data?.data ?? []
	const isTabletUp = useIsTabletUp()

	// The screen root carried only `data-screen` — the stack spacing came from
	// expert-queries-dashboard.css, which this rebuild deleted, so every block
	// on this screen sat flush against the next.
	return (
		<div className="flex flex-col gap-6">
			<div className="flex flex-col gap-1">
				<PageTitle>All queries</PageTitle>
				{queries.data ? (
					<p className="text-meta text-muted">
						<strong className="text-foreground">
							{queries.data.pagination.totalCount}
						</strong>{" "}
						{queries.data.pagination.totalCount === 1 ? "query" : "queries"} match the
						current filters
					</p>
				) : null}
			</div>

			<QueryFilters />

			{/* The wrapper was itself a 16px skeleton bar WITH five more inside
			    it, so the loading state was one clipped sliver. */}
			{queries.isPending ? (
				<div
					className="flex flex-col gap-2"
					role="status"
					aria-label="Loading queries"
				>
					{[0, 1, 2, 3, 4].map(row => (
						<Skeleton
							key={row}
							className="h-10 w-full"
						/>
					))}
				</div>
			) : null}

			{/* Against `.notice.notice--danger` (screens/expert-queries-dashboard.html)
			    rather than the shared `ErrorState`, so the retry lands inside the
			    design's own `.notice-actions` row instead of the generic kit's. */}
			{queries.isError ? (
				<DashboardError
					error={queries.error}
					onRetry={() => void queries.refetch()}
				/>
			) : null}

			{queries.data && rows.length === 0 ? (
				<EmptyState
					title={hasActiveFilters(filters) ? copy.empty.filters2 : copy.empty.stateBlock}
					description={
						hasActiveFilters(filters)
							? // The applied filters are already named as removable chips in
								// the bar above — repeating them here would say the same
								// sentence twice.
								"No query in the shared pool matches the filters above."
							: copy.empty.stateActions
					}
					action={
						hasActiveFilters(filters) ? (
							<Button
								variant="secondary"
								size="sm"
								onClick={clearFilters}
							>
								Clear filters
							</Button>
						) : undefined
					}
				/>
			) : null}

			{rows.length > 0 ? (
				<>
					{/* STACKED RECORDS BELOW THE TABLET WIDTH. Seven columns is 745px
					    of table in a 326px scroller, so 419px — the category, the
					    submitted date, the status and the first-response time —
					    were off the right edge on every phone, with `scrollbar-hide`
					    removing the cue that anything was there. tokens.css §7:
					    "tables become stacked records" at the handset width. */}
					{!isTabletUp ? (
						<RecordList
							items={rows}
							label="Queries"

							getKey={row => row.id}
							renderTitle={row => (
								<span className="flex min-w-0 items-center">
									<span
										className="max-w-full truncate font-bold"
										title={row.title}
									>
										{row.title}
									</span>
									<ItemBadge count={row.unseenCount} />
								</span>
							)}
							onItemClick={row => {
								void router.navigate({
									to: "/queries/$queryId",
									params: { queryId: String(row.sequence) },
								})
							}}
							fields={[
								{
									label: "Query",
									render: row => (
										<span className="tabular-nums">
											{formatQueryId(row.sequence)}
										</span>
									),
								},
								{
									label: "User",
									render: row => (
										<span className="flex flex-col">
											<span>
												<UserWithOwnNumber
													name={row.userName}
													userReference={row.userReference}
												/>
											</span>
											<span className="text-meta text-muted">
												{USER_TYPE_LABELS[row.userType]}
											</span>
										</span>
									),
								},
								{
									label: "Category",
									render: row => queryCategoryLabel(row.category),
								},
								{
									label: "Submitted",
									render: row => (
										<StackedInstant instant={parseTimestamp(row.submittedAt)} />
									),
								},
								{
									label: "Status",
									render: row => workStatusLabel(row.status),
								},
								{
									label: "First response",
									render: row => (
										<ElapsedCell
											submittedAt={row.submittedAt}
											firstResponseAt={row.firstResponseAt}
										/>
									),
								},
							]}
						/>
					) : null}

					{isTabletUp ? (
						<TableScroller aria-label="Queries">
							<Table>
								<TableHeader>
									<TableRow>
										{/* SIX COLUMNS ASK FOR WHAT THEY NEED; ONE TAKES THE
									    REST. Auto layout was handing 112px to "TDS", 130px
									    to "20h" and 166px to a timestamp, then squeezing
									    the 68-character title into 300px where it wrapped
									    to three lines and made every row 96px tall. */}
										<TableHead col="fit">Query</TableHead>
										<TableHead col="fit">User</TableHead>
										<TableHead col="grow">Title</TableHead>
										<TableHead col="fit">Category</TableHead>
										<TableHead col="fit">Submitted</TableHead>
										<TableHead col="fit">Status</TableHead>
										{/* THE ONE COLUMN THAT IS A MEASUREMENT is the one
									    column that is right-aligned. A ragged left edge on
									    "0m" / "23h" / "1d" is what stops the
									    eye comparing them down the column.

									    `hidden lg:table-cell`, because tokens.css §7 asks
									    for the table to come back at 768 "with the
									    least-load columns dropped". At 768 this column took
									    125px while Title — the one a person actually scans
									    — was squeezed to 99px, and the table still
									    overflowed its scroller by 58px. An elapsed time is
									    the least load-bearing thing here: it is a
									    measurement of the queue, not a fact you triage a
									    row by. It returns at 1024. */}
										<TableHead
											col="fit"
											className="hidden text-right lg:table-cell"
										>
											First response
										</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{rows.map(row => {
										const submitted = parseTimestamp(row.submittedAt)
										return (
											<TableRow
												key={row.id}
												className="relative cursor-pointer"
											>
												<TableCell className="whitespace-nowrap">
													<Link
														className="focus-visible:ring-ring absolute inset-0 z-10 rounded-[inherit] focus-visible:ring-2 focus-visible:outline-none"
														to="/queries/$queryId"
														params={{ queryId: String(row.sequence) }}
														aria-label={`Open query ${formatQueryId(row.sequence)}: ${row.title}`}
													/>
													<span className="text-meta text-muted tabular-nums">
														{formatQueryId(row.sequence)}
													</span>
												</TableCell>
												<TableCell className="whitespace-nowrap">
													<span className="flex flex-col">
														<span className="text-description text-foreground">
															<UserWithOwnNumber
																name={row.userName}
																userReference={row.userReference}
															/>
														</span>
														<span className="text-meta text-muted">
															{USER_TYPE_LABELS[row.userType]}
														</span>
													</span>
												</TableCell>
												<TableCell className="max-w-[28ch]">
													<span className="flex min-w-0 items-center">
														<span className="truncate font-bold">
															{row.title}
														</span>
														<ItemBadge count={row.unseenCount} />
													</span>
												</TableCell>
												<TableCell className="whitespace-nowrap">
													{queryCategoryLabel(row.category)}
												</TableCell>
												{/* STACKED, NOT STRUNG OUT. "25 Aug 2026, 20:36 IST"
											    on one line is the widest cell in the table and
											    none of that width carries meaning — the row is
											    already two lines tall because of the User cell
											    beside it, so the second line costs nothing and
											    returns about 70px to the title. */}
												<TableCell className="text-meta text-muted whitespace-nowrap">
													<StackedInstant instant={submitted} />
												</TableCell>
												<TableCell className="text-description text-foreground">
													{workStatusLabel(row.status)}
												</TableCell>
												{/* Paired with its header above: both carry the
											    same `hidden lg:table-cell`, so the column
											    appears and disappears as one. */}
												<TableCell className="hidden text-right whitespace-nowrap lg:table-cell">
													<ElapsedCell
														submittedAt={row.submittedAt}
														firstResponseAt={row.firstResponseAt}
													/>
												</TableCell>
											</TableRow>
										)
									})}
								</TableBody>
							</Table>
						</TableScroller>
					) : null}

					{/* OUTSIDE the scroller. Pagination inside it scrolls sideways
				    with the table, so on a phone the page controls wander off
				    the screen with the far columns. */}
					{queries.data ? (
						<Pagination
							pagination={queries.data.pagination}
							page={page}
							onPageChange={setPage}
						/>
					) : null}
				</>
			) : null}
		</div>
	)
}

/**
 * The dashboard's own fetch-error notice, against `.notice.notice--danger` /
 * `.notice-actions` (US-01-001.10's list has no other failure state).
 */
function DashboardError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
	const displayable = toDisplayable(error)

	return (
		<div
			className="rounded-card border-status-breach-border bg-status-breach-bg flex items-start gap-3 border p-3"
			role="alert"
		>
			<div>
				<h3 className="text-card-title text-foreground">{displayable.title}</h3>
				<p className="text-description text-subtle-foreground">{displayable.message}</p>
				{displayable.canRetry ? (
					<div className="mt-1 flex flex-wrap gap-2">
						<Button
							variant="secondary"
							size="sm"
							onClick={onRetry}
						>
							Try again
						</Button>
					</div>
				) : null}
			</div>
		</div>
	)
}
